import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { ServiceCard } from "@/pages/Landing";
import { Search } from "lucide-react";

export default function Browse() {
  const [sp, setSp] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [services, setServices] = useState([]);
  const [q, setQ] = useState(sp.get("q") || "");
  const activeCat = sp.get("category") || "";
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCategories(data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (activeCat) params.category = activeCat;
    if (sp.get("q")) params.q = sp.get("q");
    api.get("/services", { params })
      .then(({ data }) => setServices(data))
      .finally(() => setLoading(false));
  }, [activeCat, sp]);

  const submitSearch = (e) => {
    e.preventDefault();
    const next = new URLSearchParams(sp);
    if (q) next.set("q", q); else next.delete("q");
    setSp(next);
  };

  const setCat = (slug) => {
    const next = new URLSearchParams(sp);
    if (slug) next.set("category", slug); else next.delete("category");
    setSp(next);
  };

  const activeCategory = useMemo(() => categories.find((c) => c.slug === activeCat), [categories, activeCat]);

  return (
    <div className="space-y-8" data-testid="browse-page">
      <div className="rounded-3xl border border-foreground/10 bg-white p-6 sm:p-10">
        <h1 className="font-heading text-3xl font-extrabold tracking-tight sm:text-4xl">
          {activeCategory ? activeCategory.name : "Explore services"}
        </h1>
        <p className="mt-2 max-w-lg text-sm text-muted-foreground">
          {activeCategory ? `Best ${activeCategory.name.toLowerCase()} providers near you.` : "Search across every category — from cleaning to matrimonial."}
        </p>

        <form onSubmit={submitSearch} className="mt-6 flex max-w-xl items-center gap-2 rounded-full border border-foreground/15 bg-muted px-4 py-2">
          <Search size={16} className="text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Try 'kitchen plumbing' or 'salon at home'"
            className="border-0 bg-transparent focus-visible:ring-0"
            data-testid="search-input"
          />
          <button type="submit" className="rounded-full bg-brand-ink px-4 py-1.5 text-sm font-semibold text-white" data-testid="search-btn">
            Search
          </button>
        </form>
      </div>

      {/* Category chips */}
      <div className="flex gap-2 overflow-x-auto pb-2 no-scrollbar" data-testid="category-chips">
        <button
          onClick={() => setCat("")}
          data-testid="chip-all"
          className={`shrink-0 rounded-full border px-4 py-2 text-xs font-semibold ${
            !activeCat ? "border-brand-ink bg-brand-ink text-white" : "border-foreground/10 bg-white text-foreground/70"
          }`}
        >
          All
        </button>
        {categories.map((c) => (
          <button
            key={c.id}
            onClick={() => setCat(c.slug)}
            data-testid={`chip-${c.slug}`}
            className={`shrink-0 rounded-full border px-4 py-2 text-xs font-semibold ${
              activeCat === c.slug ? "border-brand-ink bg-brand-ink text-white" : "border-foreground/10 bg-white text-foreground/70"
            }`}
          >
            {c.name}
          </button>
        ))}
      </div>

      {/* Grid */}
      {loading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="animate-pulse rounded-2xl border bg-white">
              <div className="aspect-[16/10] rounded-t-2xl bg-muted" />
              <div className="space-y-2 p-4">
                <div className="h-3 w-24 rounded bg-muted" />
                <div className="h-4 w-3/4 rounded bg-muted" />
                <div className="h-3 w-full rounded bg-muted" />
              </div>
            </div>
          ))}
        </div>
      ) : services.length === 0 ? (
        <div className="rounded-2xl border border-dashed p-12 text-center text-sm text-muted-foreground" data-testid="no-services">
          No services found. Try a different category or search term.
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="services-grid">
          {services.map((s) => <ServiceCard key={s.id} service={s} />)}
        </div>
      )}
    </div>
  );
}
